import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../lib/cn";
import { TituloComAjuda } from "./ajuda";

/** Os avisos (sonner) ficam fora do diálogo: clicar neles — "Desfazer", o X — não fecha o que
 *  está por baixo. Sem isso, fechar um aviso derrubava um formulário aberto. */
export const ehCliqueEmAviso = (e: Event) =>
  (e.target as HTMLElement | null)?.closest?.("[data-sonner-toaster]") != null;

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-zinc-950/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /** Esconde o botao "X" no canto (uso em fluxos obrigatorios como onboarding). */
  hideClose?: boolean;
  /** Classe extra pro overlay (ex: remover backdrop-blur em confirms leves). */
  overlayClassName?: string;
  /** Nome acessível do "X". Default genérico; vale especificar quando há mais de uma coisa
   *  fechável em cena ("Fechar detalhes da consulta"). */
  closeLabel?: string;
};

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideClose = false, overlayClassName, closeLabel = "Fechar", onInteractOutside, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      // `max-h`/`overflow-y` no base, não em cada diálogo: sendo `fixed` com translate de -50%,
      // conteúdo mais alto que a janela transbordava pras DUAS pontas e ficava inalcançável — sem
      // barra de rolagem, sem ancestral rolável, e a página atrás também não rola. Em 1366x768 o
      // diálogo de nova consulta chegava a 848px numa janela de 657px e o botão de salvar ficava
      // fora da tela. Diálogos que gerenciam o próprio scroll (ficha do paciente, onboarding)
      // continuam mandando: passam `max-h`/`overflow` próprios e o twMerge deixa o deles vencer.
      //
      // `w-[calc(100%-2rem)]` e não `w-full` pelo mesmo motivo: sendo `fixed`, o 100% é a janela,
      // então em 390px o diálogo encostava nas duas bordas — canto arredondado cortado e texto a
      // 24px da beirada. Com a folga sobram 16px de cada lado; no desktop o `max-w` segue mandando.
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid max-h-[90vh] w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-xl border border-zinc-200 bg-white p-6 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        className,
      )}
      {...props}
      onInteractOutside={(e) => {
        if (ehCliqueEmAviso(e)) e.preventDefault();
        onInteractOutside?.(e);
      }}
    >
      {children}
      {/* O X é o alvo de toque mais crítico do diálogo e era só o ícone: 16x16, abaixo até do
          mínimo da WCAG 2.2 (24px). Agora são 44px — o alvo recomendado pela WCAG 2.5.5 —, com o
          ícone centrado onde sempre esteve: a caixa cresce pra fora do ícone, nada se move. Como a
          caixa é maior que o ícone, título comprido pode passar por baixo dela — quem tem título
          longo reserva a faixa com `pr-*` no próprio cabeçalho.
          `focus-visible` e não `focus`: com `focus` o anel aparecia sozinho ao abrir o diálogo, o
          que lê como estado travado em vez de navegação por teclado. */}
      {!hideClose && (
        <DialogPrimitive.Close
          aria-label={closeLabel}
          title="Fechar"
          className="absolute right-0.5 top-0.5 flex h-11 w-11 items-center justify-center rounded-md text-zinc-500 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none dark:text-muted-foreground"
        >
          <X className="h-4 w-4" />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

export const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

/**
 * Botões lado a lado, inclusive no celular. Antes o `flex-col-reverse` empilhava abaixo de `sm`, e
 * como ele inverte a ordem, a ação principal ia parar no ALTO da pilha — mais longe do polegar e
 * fora da ordem de leitura. Lado a lado resolve os dois. `flex-wrap` é a rede de segurança: rótulo
 * comprido demais quebra pra linha de baixo em vez de estourar o diálogo.
 *
 * Desktop não muda: já era `sm:flex-row sm:justify-end`, e `gap-2` rende o mesmo que `space-x-2`.
 */
export const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-row flex-wrap justify-end gap-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

type TituloProps = {
  /** Explicação curta de como o diálogo funciona — aparece num "?" ao lado do título. */
  ajuda?: React.ReactNode;
  /** Classe do grupo título + "?" (ex.: `justify-center` em título centralizado, `flex-1` em cabeçalho flex). */
  classeDoGrupo?: string;
};

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & TituloProps
>(({ className, ajuda, classeDoGrupo, ...props }, ref) => {
  const titulo = <DialogPrimitive.Title ref={ref} className={cn("text-lg font-semibold leading-none tracking-tight", ajuda && "min-w-0", className)} {...props} />;
  return ajuda ? <TituloComAjuda ajuda={ajuda} classeDoGrupo={classeDoGrupo}>{titulo}</TituloComAjuda> : titulo;
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
